import { useState, useEffect, useMemo } from 'react'
import { useCodeValues } from './use-code-values'
import { convertToCalculatorProvince, getDefaultOntarioProvince } from '@/lib/utils/province-utils'
import { CalculatorProvince } from '@/lib/types/calculator'

/**
 * Hook for getting Canadian subdivisions/provinces using the code values system
 * Replaces the class-based CodeApiService approach with hook-based code values
 * Uses table name: geo.CodeSubdivision with parentId filtered to 124 (Canada)
 */
export function useCanadianSubdivisions() {
  const [provinces, setProvinces] = useState<CalculatorProvince[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  const codeValues = useCodeValues()

  // Get Canadian subdivisions using the code values hook
  const loadCanadianSubdivisions = useMemo(() => {
    if (!codeValues.isReady) return null

    try {
      // Get subdivisions where parentId = 124 (Canada)
      // Using geo.CodeSubdivision table via the CodeSubdivision group
      const canadianSubdivisions = codeValues.getRelatedCodeValues(
        'geo.CodeSubdivision', // Table name as group
        124, // Canada's ID as parentId
        'geo.CodeCountry' // Parent group name (fixed)
      )

      if (!canadianSubdivisions || canadianSubdivisions.length === 0) {
        console.warn('No Canadian subdivisions found, falling back to default provinces')
        return [getDefaultOntarioProvince()]
      }

      // Convert code values to calculator provinces
      const calculatorProvinces = canadianSubdivisions
        .map(subdivision => {
          // Convert CodeItem to SubdivisionCodeValue format for the converter
          const subdivisionValue = {
            ...subdivision,
            parentId: subdivision.parentId || 0 // Ensure parentId is not undefined
          }
          return convertToCalculatorProvince(subdivisionValue)
        })
        .filter(province => province !== null)
        .sort((a: CalculatorProvince, b: CalculatorProvince) => a.sortOrder - b.sortOrder)

      return calculatorProvinces

    } catch (error) {
      console.error('❌ Failed to load Canadian subdivisions from code values:', error)
      return [getDefaultOntarioProvince()]
    }
  }, [codeValues.isReady, codeValues.getRelatedCodeValues])

  // Update provinces when code values are ready or data changes
  useEffect(() => {
    if (!codeValues.isReady) {
      return // Don't set loading state if not ready
    }

    const provinces = loadCanadianSubdivisions
    if (provinces) {
      setProvinces(provinces)
      setLoading(false)
      setError(null)
    }
  }, [codeValues.isReady, loadCanadianSubdivisions])

  // Memoize the return value to prevent unnecessary re-renders
  return useMemo(() => ({
    provinces,
    loading,
    error,
    isReady: codeValues.isReady,
    reload: () => {
      // Trigger a reload by clearing and reloading
      setProvinces([])
      setLoading(true)
      setError(null)
    }
  }), [provinces, loading, error, codeValues.isReady])
} 